import { createApi } from "@reduxjs/toolkit/query/react";
import customBaseQuery from "../../../../utils/customBaseQuery";
import { gql } from "graphql-request";

let defaultQueryForRecommendedLP = `
data {
  totalCount
  pageSize
  pageNumber
  searchResult {
    id
    title
    sourcetype
    sourcename
    duration
    difficultylevel
    branding {
      thumbnail
    }
    brandingurls {
      thumbnail
    }
  }`;

let queryForRecommendationsUsingSkills = `
data {
  totalCount
  pageSize
  pageNumber
  searchResult {
    id
    title
    externalownerlist
    updatedby
    brandingurls {
      promo
      thumbnail
      banner
    }
    branding {
      promo
      thumbnail
      banner
    }
    language
    stakeholder {
      owner
      observer
      nominationApprover
      reviewer
    }
    createdby
    shortdescription
    createdat
    status
    publishedat
    sourcename
    sourcetype
    artifacttype
    skills {
      type
      skillid
      parentskill
      preferredskillname {
        en_UK
        en_US
      }
    }
    searchtags {
      defaultTag
    }
    joblevel
    privateusergroup
    startdate
    proficiency
    mode
    sessioncount
    modulecount
    sectioncount
    contentcount
    episodecount
    contentlist {
      title
    }
    section {
      id
    }
    function
    isblocking
    publishedby
    maxcapacity
    visibility
    registrationcost {
      amount
      currency
    }
    templateid
    enddate
    createdzoneid
    zoneid
    nominationenddate
    role
    registrationtype
    updatedzoneid
    nominationeligibilty {
      role
      function
      jobLevel
      proficiency
    }
    mincapacity
    socialinteractions {
      contentId
      contentType
      interactionCriteria
      trigger
    }
    clonedid
    cohortid
    org
    nominationstartdate
    nominationenddate
    location
    sessioncount
    modulecount
    sectioncount
    contentcount
    episodecount
    config {
      reminderConfig {
        duration
        trigger
      }
    }
    updatedat
    waitlistpercent
    eventtype
    owners {
      id
      email
    }
    duration
    createdbyid
    weblink
    updatedbyid
    targetaudience
    difficultylevel
    learningobjective
    catalogclassification
    launchdate
    expirydate
    pageno
    launchtype
    feedback
    ratings {
      rating
      allow_ratings
      id
    }
    artifactid
  }
`;

export const LearningPathApiSlice = createApi({
  reducerPath: "learningPathApiSlice",
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    fetchLearningPathById: builder.query({
      query: ({ id, preview }): any => ({
        body: gql`
          query FetchLearningPathById($id: String!, $preview: Boolean!) {
            fetchLearningPathById(id: $id, preview: $preview) {
              error
              data {
                cataloged
                isCertificate
                id
                title
                shortDescription
                longDescription
                sourceName
                sourceType
                searchTags {
                  defaultTag
                  contentCategory
                  contentSubCategory
                  contentUsage
                  contentRegion
                  contentDomain
                }
                branding {
                  banner
                  thumbnail
                }
                brandingurls {
                  banner
                  thumbnail
                }
                sectionList {
                  id
                  title
                  shortDescription
                  skills
                  status
                  locked
                  accessEnabled
                  lockedSectionId
                  lockedContentId
                  connectionList {
                    id
                    sourceSectionId
                    nextSectionId
                    title
                  }
                  contentList {
                    id
                    title
                    logo
                    shortdescription
                    artifacttype
                    artifacturl
                    sourcename
                    duration
                    startdate
                    updatedat
                    branding {
                      thumbnail
                    }
                    brandingurls {
                      thumbnail
                    }
                    locked
                    lockedType
                    accessCriteria {
                      joblevel
                      progress
                      userAccess {
                        cohort
                        userId
                        userGroup
                      }
                    }
                    accessEnabled
                    joblevel
                    proficiency
                    lockedSectionId
                    lockedContentId
                  }
                }
                socialInteractions {
                  contentId
                  contentType
                  interactionCriteria
                }
                prerequisite
                learningObjective
                demonstrableOutcome
                skills
                skillnamelist
                stakeholder {
                  owner
                }
                difficultyLevel
                duration
                type
                category
                access
                launchDate
                expiryDate
                updatedat
                language
                contentOccurences
              }
            }
          }
        `,
        url: "/graphql",
        params: {
          variables: {
            id: id,
            preview: preview
          }
        }
      }),
      extraOptions: { service: "Catalog" }
    }),
    fetchAllLearningPaths: builder.query({
      query: (pageNumber): any => ({
        body: gql`
          query {
            fetchAllLearningPaths(
              pageNo: ${pageNumber}
              pageSize: 15
              sortKey: "updatedat"
              sortOrder: "ASC"
            ) {
              error
              data {
                totalCount
                pageSize
                pageNo
                searchResult {
                  id
                  title
                  sessioncount
                  modulecount
                  sectioncount
                  contentcount
                  episodecount
                  branding {
                    thumbnail
                  }
                  brandingurls {
                    thumbnail
                  }
                  sourceType
                  sourceName
                  duration
                  difficultyLevel
                  access
                }
              }
            }
          }
        `,
        url: "/graphql"
      }),
      extraOptions: { service: "Catalog" },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCacheData, responseData, otherArgs) => {
        if (otherArgs.arg > 0) {
          const messageMap = new Map();
          [
            ...currentCacheData.fetchAllLearningPaths.data.searchResult,
            ...responseData.fetchAllLearningPaths.data.searchResult
          ].map((msg) => {
            if (!messageMap.has(msg.id)) {
              messageMap.set(msg.id, msg);
            }
          });
          const mergedResult = [...messageMap.values()];
          return {
            ...responseData,
            fetchAllLearningPaths: {
              ...responseData.fetchAllLearningPaths,
              data: {
                ...responseData.fetchAllLearningPaths.data,
                searchResult: mergedResult
              }
            }
          };
        } else return responseData;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      }
    }),
    fetchLearningPathProgress: builder.query({
      query: ({ learningPathId }): any => ({
        body: gql`
          query FetchLearningPathProgress($learningPathId: String!) {
            fetchLearningPathProgress(learningPathId: $learningPathId) {
              error
              data {
                progressStatus
                learningPathId
                userId
                progressPercentage
                status
                sectionList {
                  sectionId
                  progressPercentage
                  completedat
                  locked
                  lockedType
                  status
                  statusMessage
                  contentList {
                    contentId
                    progressPercentage
                    locked
                    lockedType
                    completedat
                    completedBySystem
                    status
                    statusMessage
                    securedScore
                  }
                }
              }
            }
          }
        `,
        url: "/graphql",
        params: {
          variables: {
            learningPathId: learningPathId
          }
        }
      }),
      extraOptions: { service: "Progress" }
    }),
    fetchLearningPathUsingSkills: builder.query({
      query: ({ pageNumber, skills, allArtifacts }): any => ({
        url: "/graphql",
        body: gql`
          query FetchLearningPathUsingSkills(
            $pageNumber: Int!
            $skills: [String]!
            $indices: [String]!
            $artifactType: [String]!
          ) {
            globalSearch(
              requestDto: {
                filters: { skills: $skills, artifactType: $artifactType }
                searchRequest: ""
                indices: $indices
                smartSearch: false
              }
              pageNumber: $pageNumber
              pageSize: 15
              sortKey: "updatedat"
              sortOrder: "ASC"
              relevance: true
            ) {
              error
              ${allArtifacts ? queryForRecommendationsUsingSkills : defaultQueryForRecommendedLP}
              }
            }
          }
        `,
        params: {
          variables: {
            pageNumber: pageNumber,
            skills: skills,
            indices: allArtifacts
              ? ["learningpath", "course", "content_modified"]
              : ["learningpath"],
            artifactType: allArtifacts ? [] : ["learningpath"]
          }
        }
      }),
      extraOptions: { service: "Catalog" },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCacheData, responseData, otherArgs) => {
        if (otherArgs.arg.pageNumber > 1) {
          const messageMap = new Map();
          [
            ...currentCacheData.globalSearch.data.searchResult,
            ...responseData.globalSearch.data.searchResult
          ].map((msg) => {
            if (!messageMap.has(msg.id)) {
              messageMap.set(msg.id, msg);
            }
          });
          const mergedResult = [...messageMap.values()];
          return {
            ...responseData,
            globalSearch: {
              ...responseData.globalSearch,
              data: {
                ...responseData.globalSearch.data,
                searchResult: mergedResult
              }
            }
          };
        } else return responseData;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      }
    }),
    assignLearningPath: builder.mutation({
      query: ({ body, type }) => ({
        body: body,
        method: "POST",
        url: `/assignment?actionType=${type}`
      }),
      extraOptions: { service: "Catalog" }
    }),
    assignDeadlineDate: builder.query({
      query: ({ artifactId, userId }) => ({
        url: `/assignment/${artifactId}/${userId}`,
        method: "get"
      }),
      extraOptions: { service: "Catalog" }
    }),
    createProgress: builder.mutation({
      query: ({ id }) => ({
        url: `/progress/lp/create/${id}`,
        method: "GET"
      }),
      extraOptions: { service: "Progress" }
    }),
    getLPLearnerCountApi: builder.query({
      query: (contentId) => {
        return {
          url: `/progress/learnerCount/${contentId}`,
          method: "GET"
        };
      },
      extraOptions: { service: "Progress" }
    }),
    fetchLpLaunchDateAndTitleById: builder.query({
      query: ({ id, preview }): any => ({
        body: gql`
          query FetchLearningPathById($id: String!, $preview: Boolean!) {
            fetchLearningPathById(id: $id, preview: $preview) {
              data {
                id
                title
                launchDate
              }
            }
          }
        `,
        url: "/graphql",
        params: {
          variables: {
            id: id,
            preview: preview
          }
        }
      }),
      extraOptions: { service: "Catalog" }
    }),
    getLpProgressStatus: builder.query({
      query: (id: any) => {
        return {
          url: `/progress/polling/learningpath/${id}`,
          method: "GET"
        };
      },
      extraOptions: { service: "Progress" }
    })
  })
});

export const {
  useLazyFetchLearningPathByIdQuery,
  useFetchLearningPathByIdQuery,
  useFetchAllLearningPathsQuery,
  useLazyFetchLearningPathProgressQuery,
  useAssignLearningPathMutation,
  useLazyAssignDeadlineDateQuery,
  useCreateProgressMutation,
  useFetchLearningPathUsingSkillsQuery,
  useGetLPLearnerCountApiQuery,
  useLazyGetLPLearnerCountApiQuery,
  useLazyFetchLpLaunchDateAndTitleByIdQuery,
  useLazyGetLpProgressStatusQuery,
  useGetLpProgressStatusQuery
} = LearningPathApiSlice;
